// /reports/repeat-visits — Repeat Visits (PAGES.md §3.51)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { SalesWaveChart } from '@bbpos/ui';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';

interface BucketRow {
  id: string;
  label: string;
  customer_count: number;
}

const columns: ColumnDef<BucketRow>[] = [
  { key: 'label', header: 'Visit Frequency', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.label}</span> },
  { key: 'customer_count', header: 'Customers', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{row.customer_count.toLocaleString('en-IN')}</span> },
];

export function RepeatVisitsReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const filter = useReportFilter(timeRange, undefined);

  const { data, isLoading, error } = useReportQuery({
    key: 'repeat-visits',
    filter,
    queryFn: (f) => reportsApi.getRepeatVisits(f),
  });

  const rows: BucketRow[] = useMemo(
    () => (data?.buckets ?? []).map((b, i) => ({ id: `${b.label}-${i}`, ...b })),
    [data],
  );

  const trendData = (data?.trend ?? []).map((t) => ({ label: t.date.slice(5), today: t.customers, yesterday: 0 }));

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `repeat-visits-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Frequency', get: (r: BucketRow) => r.label },
          { header: 'Customers', get: (r: BucketRow) => r.customer_count },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Repeat Visits', `Repeat rate: ${(data?.repeat_rate_pct ?? 0).toFixed(1)}% • ${data?.total_customers ?? 0} customers`);
    }
  };

  return (
    <ReportPageShell
      title="Repeat Visits"
      subtitle="First-time vs returning customer split and retention trend over time."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Repeat Visits' }]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} showLocation={false} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={3} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'customers', title: 'Total Customers', value: (data?.total_customers ?? 0).toLocaleString('en-IN'), tone: 'indigo', icon: KpiIcons.users },
              { id: 'repeat', title: 'Repeat Rate', value: `${(data?.repeat_rate_pct ?? 0).toFixed(1)}%`, tone: 'violet', icon: KpiIcons.percent },
              { id: 'avg', title: 'Avg Visits', value: (data?.avg_visits_per_customer ?? 0).toFixed(1), tone: 'pink', icon: KpiIcons.star },
            ]}
          />
          {trendData.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <SalesWaveChart data={trendData} valueFormat="number" title="Daily Customer Count" />
            </div>
          ) : null}
          <ReportTable title="Visit Frequency Buckets" columns={columns} data={rows} emptyTitle="Not enough data for retention analysis" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
